import React, { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'
import { FoodLoading, Markdown, AioliModal, IcebreakerChips } from '@instacart/aioli-ui'
import {
  colors,
  spacing,
  borderRadius,
  fontSize,
} from '@retailer-platform/shared-components/src/foundation'
import { Button, Flex } from '@retailer-platform/shared-components/src/tds'
import useAccessControl from '../../legacy/components/AccessControl/useAccessControl'
import { FeatureToggle } from '../../legacy/contexts/featureToggles/FeatureToggleContext'
import { useDashMessages } from '../../intl/intl.hooks'
import { usePartnerId } from '../../utils/routing/params.hooks'
import { useRetailerAgentChat } from './hooks/useRetailerAgentChat'
import { useConversationHistory } from './hooks/useConversationHistory'
import { ConversationHistoryDropdown } from './ConversationHistoryDropdown'
import { ApprovalCard } from './components/ApprovalCard'
import { ApprovalCardSettled } from './components/ApprovalCardSettled'

const SIDEBAR_WIDTH = 400

const messageIds = {
  title: 'retailerAgent.title' as const,
  approval: 'retailerAgent.approval.title' as const,
  approvalConfirm: 'retailerAgent.approval.confirm' as const,
  approvalCancel: 'retailerAgent.approval.cancel' as const,
  placeholder: 'retailerAgent.chat.placeholder' as const,
  placeholderPendingApproval: 'retailerAgent.composer.pendingApproval' as const,
  promptSummarize: 'retailerAgent.prompts.summarize' as const,
  promptTopProduct: 'retailerAgent.prompts.topProduct' as const,
  promptStoreHours: 'retailerAgent.prompts.storeHours' as const,
  promptInsight: 'retailerAgent.prompts.insight' as const,
}

const slideIn = keyframes({
  from: { opacity: 0, transform: 'translateY(12px)' },
  to: { opacity: 1, transform: 'translateY(0)' },
})

const FloatingButton = styled.button({
  position: 'fixed',
  bottom: spacing.X24,
  right: spacing.X24,
  zIndex: 1600,
  width: spacing.X52,
  height: spacing.X52,
  borderRadius: '50%',
  background: colors.PRIMARY_TDS.REGULAR,
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
  animation: `${slideIn} 300ms ease-out`,
  color: colors.GRAYSCALE.X0,
  fontSize: fontSize.X23,
})

const Sidebar = styled.div<{ isOpen: boolean }>(({ isOpen }) => ({
  position: 'fixed',
  top: 0,
  right: isOpen ? 0 : -SIDEBAR_WIDTH - 32,
  width: SIDEBAR_WIDTH,
  height: '100vh',
  zIndex: 1600,
  background: colors.GRAYSCALE.X0,
  boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
  display: 'flex',
  flexDirection: 'column',
  transition: 'right 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
}))

const Header = styled.div({
  display: 'flex',
  alignItems: 'center',
  height: spacing.X56,
  padding: `0 ${spacing.X16}`,
  borderBottom: `1px solid ${colors.GRAYSCALE.X20}`,
  flexShrink: 0,
  gap: spacing.X8,
})

const MessagesPane = styled.div({
  flex: 1,
  overflowY: 'auto',
  padding: `${spacing.X16} ${spacing.X20}`,
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.X12,
})

const Bubble = styled.div<{ isUser: boolean }>(({ isUser }) => ({
  maxWidth: '80%',
  alignSelf: isUser ? 'flex-end' : 'flex-start',
  padding: `${spacing.X8} ${spacing.X12}`,
  borderRadius: isUser
    ? `${borderRadius.X12} ${borderRadius.X12} ${borderRadius.X4} ${borderRadius.X12}`
    : `${borderRadius.X12} ${borderRadius.X12} ${borderRadius.X12} ${borderRadius.X4}`,
  background: isUser ? colors.PRIMARY_TDS.DARK : colors.GRAYSCALE.X10,
  color: isUser ? colors.GRAYSCALE.X0 : colors.GRAYSCALE.X80,
  fontSize: fontSize.X14,
  lineHeight: 1.5,
  whiteSpace: isUser ? 'pre-wrap' : undefined,
}))

const StatusRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: spacing.X8,
  padding: `${spacing.X4} 0`,
  color: colors.GRAYSCALE.X60,
  fontSize: fontSize.X14,
})

const InputWrapper = styled.div({
  padding: `${spacing.X12} ${spacing.X16} ${spacing.X16}`,
  borderTop: `1px solid ${colors.GRAYSCALE.X20}`,
  flexShrink: 0,
})

const ChatTextarea = styled.textarea({
  flex: 1,
  resize: 'none',
  border: `1px solid ${colors.GRAYSCALE.X30}`,
  borderRadius: borderRadius.X8,
  padding: `${spacing.X8} ${spacing.X12}`,
  fontSize: fontSize.X14,
  lineHeight: 1.5,
  fontFamily: 'inherit',
  outline: 'none',
  maxHeight: '120px',
  overflowY: 'auto',
  ':focus': {
    borderColor: colors.PRIMARY_TDS.REGULAR,
  },
})

const SendButton = styled.button<{ isStreaming: boolean }>(({ isStreaming }) => ({
  flexShrink: 0,
  width: spacing.X36,
  height: spacing.X36,
  borderRadius: '50%',
  background: isStreaming ? colors.GRAYSCALE.X30 : colors.PRIMARY_TDS.REGULAR,
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: colors.GRAYSCALE.X0,
  fontSize: fontSize.X18,
}))

const HeaderIconButton = styled.button({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  background: 'transparent',
  color: colors.GRAYSCALE.X70,
  border: `1px solid ${colors.GRAYSCALE.X30}`,
  borderRadius: '50%',
  cursor: 'pointer',
  fontSize: fontSize.X14,
  lineHeight: 1,
  flexShrink: 0,
  ':hover': {
    background: colors.GRAYSCALE.X10,
  },
})

const HeaderActions = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: spacing.X4,
  flexShrink: 0,
})

function RetailerAgentOverlayInner() {
  const [isOpen, setIsOpen] = useState(false)
  const [sessionHistoryRefreshTrigger, setSessionHistoryRefreshTrigger] = useState(0)
  const [inputValue, setInputValue] = useState('')
  const partnerId = usePartnerId()
  const chatInputRef = useRef<HTMLTextAreaElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const messages = useDashMessages(messageIds)
  const prevChatMessageCountRef = useRef(0)

  const {
    messages: chatMessages,
    isStreaming,
    isToolRunning,
    isRestoringHistory,
    toolName,
    sendMessage,
    confirmApproval,
    cancelApproval,
    stopStreaming,
    startNewChat,
    switchToSession,
    hasPendingApproval,
  } = useRetailerAgentChat()

  const hasAccess = useAccessControl()
  const inlineApprovalFlag = hasAccess({
    featureToggles: [FeatureToggle.RetailerAgentWrites],
  })

  // WS1 Task 9 transitional shim: approvals are now first-class chat messages, but
  // the overlay still renders the legacy AioliModal until Task 14 swaps it for
  // inline ApprovalCard / ApprovalRecordCard components. Derive a modal-shaped view
  // from the latest pending approval message so existing render logic compiles.
  const pendingApproval = chatMessages.find(m => m.role === 'approval' && m.status === 'pending')
  const legacyApprovalForModal =
    pendingApproval && pendingApproval.role === 'approval'
      ? {
          suspensionToken: pendingApproval.suspensionToken,
          summary: pendingApproval.preview.summary ?? messages.approval,
          toolName: pendingApproval.preview.toolName,
        }
      : null

  const { sessions, isLoading: isLoadingHistory } = useConversationHistory(
    partnerId,
    sessionHistoryRefreshTrigger
  )

  // Increment refresh trigger when a new session is created (first assistant message appears
  // after an empty start — detectable by going from 0 messages to messages with user+assistant)
  useEffect(() => {
    const prev = prevChatMessageCountRef.current
    const curr = chatMessages.length
    prevChatMessageCountRef.current = curr

    if (prev === 0 && curr >= 2) {
      setSessionHistoryRefreshTrigger(t => t + 1)
    }
  }, [chatMessages])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chatMessages, isToolRunning, pendingApproval])

  useEffect(() => {
    if (isOpen) {
      chatInputRef.current?.focus()
    }
  }, [isOpen])

  const handleStartNewChat = useCallback(() => {
    startNewChat(partnerId)
    prevChatMessageCountRef.current = 0
  }, [startNewChat, partnerId])

  const firstUserMessage = chatMessages.find(m => m.role === 'user')
  // Narrow the discriminated union — the `approval` branch has no `content` field,
  // so we explicitly guard before accessing it.
  const currentTitle =
    firstUserMessage && firstUserMessage.role !== 'approval'
      ? firstUserMessage.content.slice(0, 60)
      : undefined

  const composerDisabled = inlineApprovalFlag ? hasPendingApproval : !!legacyApprovalForModal

  return createPortal(
    <>
      {!isOpen && (
        <FloatingButton onClick={() => setIsOpen(true)} aria-label={messages.title}>
          ✦
        </FloatingButton>
      )}

      <Sidebar isOpen={isOpen}>
        <Header>
          <ConversationHistoryDropdown
            sessions={sessions}
            isLoading={isLoadingHistory}
            currentTitle={currentTitle}
            onSelect={sessionId => {
              void switchToSession(sessionId, partnerId)
            }}
          />
          <HeaderActions>
            <HeaderIconButton
              onClick={handleStartNewChat}
              aria-label="Start new chat"
              title="Start new chat"
            >
              +
            </HeaderIconButton>
            <HeaderIconButton onClick={() => setIsOpen(false)} aria-label="Close">
              ✕
            </HeaderIconButton>
          </HeaderActions>
        </Header>

        <MessagesPane>
          {chatMessages.length === 0 && !isStreaming && !isRestoringHistory && (
            <IcebreakerChips
              items={[
                messages.promptSummarize,
                messages.promptTopProduct,
                messages.promptStoreHours,
                messages.promptInsight,
              ]}
              onSelect={text => {
                void sendMessage(text, partnerId)
              }}
              variant="wrap"
              size="sm"
            />
          )}

          {isRestoringHistory && (
            <StatusRow>
              <FoodLoading size="xs" inline showText={false} />
            </StatusRow>
          )}

          {chatMessages.map(msg => {
            if (msg.role === 'approval') {
              if (!inlineApprovalFlag) return null
              if (msg.status === 'pending') {
                return (
                  <ApprovalCard
                    key={msg.id}
                    preview={msg.preview}
                    locked={msg.locked}
                    onApprove={() => {
                      void confirmApproval(partnerId)
                    }}
                    onCancel={() => cancelApproval(partnerId)}
                  />
                )
              }
              return (
                <ApprovalCardSettled
                  key={msg.id}
                  preview={msg.preview}
                  status={msg.status}
                  settledAt={msg.settledAt ?? 0}
                />
              )
            }
            return (
              <Bubble key={msg.id} isUser={msg.role === 'user'}>
                {msg.role === 'user' ? msg.content : <Markdown>{msg.content}</Markdown>}
              </Bubble>
            )
          })}

          {isToolRunning && (
            <StatusRow>
              <FoodLoading size="xs" inline showText={false} />
              {toolName ?? '…'}
            </StatusRow>
          )}

          {isStreaming && !isToolRunning && (
            <StatusRow>
              <FoodLoading size="xs" inline showText={false} />
            </StatusRow>
          )}

          <div ref={bottomRef} />
        </MessagesPane>

        <InputWrapper>
          <Flex.Row gap={spacing.X8} alignItems="flex-end">
            <ChatTextarea
              ref={chatInputRef}
              value={inputValue}
              rows={1}
              placeholder={
                composerDisabled && inlineApprovalFlag
                  ? messages.placeholderPendingApproval
                  : messages.placeholder
              }
              disabled={composerDisabled}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={e => {
                if (
                  e.key === 'Enter' &&
                  !e.shiftKey &&
                  !isStreaming &&
                  !composerDisabled &&
                  inputValue.trim()
                ) {
                  e.preventDefault()
                  void sendMessage(inputValue.trim(), partnerId)
                  setInputValue('')
                }
              }}
            />
            <SendButton
              isStreaming={isStreaming}
              disabled={composerDisabled}
              onClick={() => {
                if (composerDisabled) return
                if (isStreaming) {
                  stopStreaming()
                } else if (inputValue.trim()) {
                  void sendMessage(inputValue.trim(), partnerId)
                  setInputValue('')
                }
              }}
              aria-label={isStreaming ? 'Stop' : 'Send'}
            >
              {isStreaming ? '■' : '↑'}
            </SendButton>
          </Flex.Row>
        </InputWrapper>
      </Sidebar>

      {!inlineApprovalFlag && (
        <AioliModal
          isOpen={!!legacyApprovalForModal}
          onClose={() => cancelApproval(partnerId)}
          title={messages.approval}
          size="sm"
          footer={
            <Flex.Row justifyContent="flex-end" gap={spacing.X8}>
              <Button variant="secondary" onClick={() => cancelApproval(partnerId)}>
                {messages.approvalCancel}
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  void confirmApproval(partnerId)
                }}
              >
                {messages.approvalConfirm}
              </Button>
            </Flex.Row>
          }
        >
          {legacyApprovalForModal?.summary}
        </AioliModal>
      )}
    </>,
    document.body
  )
}

export function RetailerAgentOverlay() {
  const hasAccess = useAccessControl()
  if (!hasAccess({ featureToggles: [FeatureToggle.RetailerAgent] })) return null
  return <RetailerAgentOverlayInner />
}
